import { useEffect, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import CircularProgress from '@mui/joy/CircularProgress'
import List from '@mui/joy/List'
import ListItem from '@mui/joy/ListItem'
import ListItemContent from '@mui/joy/ListItemContent'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Switch from '@mui/joy/Switch'
import Typography from '@mui/joy/Typography'
import { DEFAULT_MOBILE_RATE_CHECK_PERIODS, type MobileRateCheckPeriods } from '@numeo/types'
import { Link } from 'react-router-dom'
import { mobileRateCheckApi } from 'api/mobileRateCheck'

const PERIODS: Array<{ key: keyof MobileRateCheckPeriods; title: string; subtitle: string }> = [
    {
        key: 'sevenDays',
        title: '7 days',
        subtitle: 'Load-weighted average of the daily medians — needs days that each cleared the 10-load floor.',
    },
    { key: 'fourteenDays', title: '14 days', subtitle: 'The window’s real medians — needs a full two weeks of data behind the window.' },
]

const describe = (p: MobileRateCheckPeriods) => ['Today', p.sevenDays ? '7 days' : null, p.fourteenDays ? '14 days' : null].filter(Boolean).join(' · ')
const same = (a: MobileRateCheckPeriods, b: MobileRateCheckPeriods) => a.sevenDays === b.sevenDays && a.fourteenDays === b.fourteenDays

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

export default function MobileRateCheckPage() {
    // `saved` is null until the live value has actually been read: showing the default in its place would present a
    // guess as what the app is serving.
    const [saved, setSaved] = useState<MobileRateCheckPeriods | null>(null)
    const [selected, setSelected] = useState<MobileRateCheckPeriods>(DEFAULT_MOBILE_RATE_CHECK_PERIODS)
    const [loading, setLoading] = useState(true)
    const [loadFailed, setLoadFailed] = useState(false)
    const [saving, setSaving] = useState(false)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    async function load() {
        setLoading(true)
        setLoadFailed(false)
        try {
            const periods = await mobileRateCheckApi.get()
            setSaved(periods)
            setSelected(periods)
        } catch (error) {
            console.error('Failed to load the mobile Rate Check periods', error)
            setLoadFailed(true)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        void load()
    }, [])

    async function save() {
        setSaving(true)
        try {
            const periods = await mobileRateCheckApi.set(selected)
            setSaved(periods)
            setSelected(periods)
            setSnackbar({ open: true, message: 'Rate Check periods saved', color: 'success' })
        } catch (error) {
            console.error('Failed to save the mobile Rate Check periods', error)
            setSnackbar({ open: true, message: 'Failed to save the Rate Check periods', color: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 720 }}>
            <Typography level="h3" sx={{ mb: 1 }}>
                Mobile Rate Check
            </Typography>
            <Typography level="body-sm" sx={{ mb: 3 }}>
                Which periods the mobile Rate Checker offers. Today is always on. Turn 7 days and 14 days on once Market Intel holds enough data to trust them —
                check a few lanes on <Link to="/load-pipeline/market-intel/lane-prices">Lane Prices</Link> first. Takes effect on the next app launch /
                foreground; app versions older than this setting show every period.
            </Typography>

            {loadFailed ? (
                <Alert
                    color="danger"
                    sx={{ mb: 3 }}
                    endDecorator={
                        <Button size="sm" variant="soft" color="danger" onClick={() => void load()}>
                            Retry
                        </Button>
                    }
                >
                    Could not read the current setting, so it is not shown. Saving is disabled until it loads.
                </Alert>
            ) : null}

            <Card variant="outlined" sx={{ p: 0, mb: 3 }}>
                <List sx={{ '--ListItem-paddingY': '12px', '--ListItem-paddingX': '16px' }}>
                    <ListItem>
                        <ListItemContent>
                            <Typography level="title-sm">Today</Typography>
                            <Typography level="body-xs">Today’s medians, or yesterday’s until today clears the floor. Always on.</Typography>
                        </ListItemContent>
                        <Switch checked disabled />
                    </ListItem>
                    {PERIODS.map(({ key, title, subtitle }) => (
                        <ListItem key={key} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                            <ListItemContent>
                                <Typography level="title-sm">{title}</Typography>
                                <Typography level="body-xs">{subtitle}</Typography>
                            </ListItemContent>
                            <Switch
                                checked={selected[key]}
                                disabled={saving || saved === null}
                                onChange={(e) => setSelected((prev) => ({ ...prev, [key]: e.target.checked }))}
                            />
                        </ListItem>
                    ))}
                </List>
            </Card>

            <Stack direction="row" spacing={2} alignItems="center">
                <Button onClick={save} disabled={saving || saved === null || same(selected, saved)} loading={saving}>
                    Save
                </Button>
                {saved === null ? (
                    <Typography level="body-xs">Live: unknown</Typography>
                ) : same(selected, saved) ? (
                    <Typography level="body-xs">Live: {describe(saved)}</Typography>
                ) : (
                    <Typography level="body-xs">Unsaved — live is still {describe(saved)}</Typography>
                )}
            </Stack>

            <Typography level="body-xs" sx={{ mt: 3, color: 'text.tertiary' }}>
                Path: <code>numeo_config.mobileRateCheck</code>
            </Typography>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                color={snackbar.color}
                variant="soft"
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
